"use client";

import { useEffect, useState } from "react";
import Script from "next/script";
import { PORTE_OPTIONS } from "./content";
import { getMetaClickIds, persistMetaLead, trackPixelEvent } from "@/lib/meta/browser";
import styles from "./MoovLpForm.module.css";

type Variant = "hero" | "final";
type PageKind = "texto" | "video";

interface MoovLpFormProps {
    idPrefix: string;
    page: PageKind;
    variant?: Variant;
    loadRecaptcha?: boolean;
}

export default function MoovLpForm({ idPrefix, page, variant = "final", loadRecaptcha = true }: MoovLpFormProps) {
    const [name, setName] = useState("");
    const [company, setCompany] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [porte, setPorte] = useState("");
    const [honeypot, setHoneypot] = useState("");
    const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const [utmSource, setUtmSource] = useState("");
    const [utmMedium, setUtmMedium] = useState("");
    const [utmCampaign, setUtmCampaign] = useState("");
    const [utmContent, setUtmContent] = useState("");
    const [utmTerm, setUtmTerm] = useState("");

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        setUtmSource(queryParams.get("utm_source") || "");
        setUtmMedium(queryParams.get("utm_medium") || "");
        setUtmCampaign(queryParams.get("utm_campaign") || "");
        setUtmContent(queryParams.get("utm_content") || "");
        setUtmTerm(queryParams.get("utm_term") || "");
    }, []);

    const formatPhone = (value: string) => {
        const numbers = value.replace(/\D/g, "").slice(0, 11);
        if (numbers.length <= 2) return numbers;
        if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    };

    const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const resetForm = () => {
        setName("");
        setCompany("");
        setPhone("");
        setEmail("");
        setPorte("");
        setStatus("idle");
        setErrorMessage("");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMessage("");

        if (honeypot.trim() !== "") {
            if (page === "texto") {
                window.location.assign("/lpmoov1/obrigado");
                return;
            }
            setStatus("success");
            return;
        }

        if (phone.replace(/\D/g, "").length < 10) {
            setStatus("error");
            setErrorMessage("Por favor, insira um telefone válido com DDD.");
            return;
        }

        if (!isValidEmail(email)) {
            setStatus("error");
            setErrorMessage("Por favor, insira um e-mail válido.");
            return;
        }

        try {
            const recaptchaToken = await new Promise<string>((resolve, reject) => {
                const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
                if (!siteKey) {
                    resolve("bypass_for_development");
                    return;
                }

                const tryExecute = (retries = 0) => {
                    if (window.grecaptcha?.enterprise) {
                        window.grecaptcha.enterprise.ready(() => {
                            window.grecaptcha!.enterprise!
                                .execute(siteKey, { action: "submit" })
                                .then(resolve)
                                .catch(() => reject(new Error("Falha ao gerar token de segurança.")));
                        });
                    } else if (retries < 10) {
                        setTimeout(() => tryExecute(retries + 1), 500);
                    } else {
                        reject(new Error("O sistema anti-spam não carregou. Recarregue a página e tente novamente."));
                    }
                };
                tryExecute();
            });

            const assunto = page === "texto" ? "Contato via LP Moov Texto" : "Contato via LP Moov Video";
            const meta = getMetaClickIds();

            const response = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    nome: name,
                    telefone: phone,
                    email,
                    assunto,
                    contratos: porte,
                    mensagem: `Imobiliária: ${company}`,
                    categoria: 1351,
                    origem: "Site",
                    etiqueta: "Meta Ads",
                    utmSource,
                    utmMedium,
                    utmCampaign,
                    utmContent,
                    utmTerm,
                    _honeypot: honeypot,
                    recaptchaToken,
                    eventId: meta.eventId,
                    fbp: meta.fbp,
                    fbc: meta.fbc,
                    eventSourceUrl: meta.eventSourceUrl,
                }),
            });

            const data = await response.json().catch(() => {
                throw new Error("Não foi possível confirmar o envio da mensagem. Tente novamente.");
            });

            if (!response.ok || data?.success !== true || data?.error) {
                throw new Error("Não foi possível confirmar o envio da mensagem. Tente novamente.");
            }

            if (data?.data?.status === "bot_ignored") {
                if (page === "texto") {
                    window.location.assign("/lpmoov1/obrigado");
                    return;
                }
                setStatus("success");
                return;
            }

            persistMetaLead({ email, phone, name });
            trackPixelEvent("Lead", data.eventId || meta.eventId, {
                content_name: assunto,
                content_category: "lead",
                status: "submitted",
            });

            if (page === "texto") {
                window.location.assign("/lpmoov1/obrigado");
                return;
            }

            setStatus("success");
            setName("");
            setCompany("");
            setPhone("");
            setEmail("");
            setPorte("");
        } catch (error) {
            setStatus("error");
            setErrorMessage(error instanceof Error ? error.message : "Erro crítico ao conectar com o servidor.");
        }
    };

    const isHero = variant === "hero";

    return (
        <div className={styles.card}>
            {loadRecaptcha && process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
                <Script
                    id="recaptcha-enterprise"
                    src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
                    strategy="afterInteractive"
                />
            )}
            {status === "success" ? (
                <div className={styles.success}>
                    <h3>Mensagem enviada!</h3>
                    <p className={styles.lead}>Obrigado pelo contato. Nosso time retornará em breve.</p>
                    <button type="button" className={styles.reset} onClick={resetForm}>
                        Enviar nova mensagem
                    </button>
                </div>
            ) : (
                <>
                        <div className={styles.eyebrow}>
                            {isHero ? "Demonstração personalizada" : "Agende sua demonstração"}
                        </div>
                        <h3>{isHero ? "Veja como o Moov funcionaria dentro da sua imobiliária." : "Quero conhecer o Moov."}</h3>
                        <p className={styles.lead}>
                            {isHero
                                ? "Conte um pouco sobre sua operação e nosso time prepara uma demonstração focada nos seus processos, equipe e desafios."
                                : "Preencha os dados e nosso time entra em contato."}
                        </p>

                        <form onSubmit={handleSubmit}>
                            <input
                                className={styles.honeypot}
                                type="text"
                                name="_honeypot"
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                value={honeypot}
                                onChange={(e) => setHoneypot(e.target.value)}
                            />

                            <div className={styles.grid}>
                                <div className={styles.field}>
                                    <label htmlFor={`${idPrefix}-nome`}>Nome</label>
                                    <input
                                        id={`${idPrefix}-nome`}
                                        name="nome"
                                        type="text"
                                        placeholder="Seu nome"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor={`${idPrefix}-imobiliaria`}>Imobiliária</label>
                                    <input
                                        id={`${idPrefix}-imobiliaria`}
                                        name="imobiliaria"
                                        type="text"
                                        placeholder="Nome da imobiliária"
                                        value={company}
                                        onChange={(e) => setCompany(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor={`${idPrefix}-whatsapp`}>WhatsApp</label>
                                    <input
                                        id={`${idPrefix}-whatsapp`}
                                        name="whatsapp"
                                        type="tel"
                                        placeholder="(00) 00000-0000"
                                        value={phone}
                                        onChange={(e) => setPhone(formatPhone(e.target.value))}
                                        required
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor={`${idPrefix}-email`}>E-mail</label>
                                    <input
                                        id={`${idPrefix}-email`}
                                        name="email"
                                        type="email"
                                        placeholder="voce@imobiliaria.com.br"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className={`${styles.field} ${styles.full}`}>
                                    <label htmlFor={`${idPrefix}-porte`}>Tamanho da operação</label>
                                    <select
                                        id={`${idPrefix}-porte`}
                                        name="porte"
                                        required
                                        value={porte}
                                        onChange={(e) => setPorte(e.target.value)}
                                    >
                                        <option value="" disabled>
                                            Selecione uma faixa
                                        </option>
                                        {PORTE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {status === "error" && (
                                    <p className={`${styles.error} ${styles.full}`}>{errorMessage}</p>
                                )}
                                <div className={`${styles.field} ${styles.full}`}>
                                    <button className={styles.submit} type="submit" disabled={status === "loading"}>
                                        {status === "loading" ? "Enviando..." : "Quero ver o Moov na prática →"}
                                    </button>
                                </div>
                            </div>
                            <p className={styles.note}>Ao enviar, você concorda em receber contato da Flexpro sobre esta demonstração.</p>
                        </form>
                    </>
                )}
        </div>
    );
}
